import { z } from 'zod';
import { PCMP_CHANNELS } from './pcmpProposal.types';
import {
  pcmpPageMetaSchema,
  pcmpQueueSearchShape,
  pcmpSkippedProposalSchema,
  type PcmpQueueFilters,
} from './pcmpQueue.types';
import {
  pcmpRequestQueueItemSchema,
  pcmpRequestStatusSchema,
  pcmpRequestSummarySchema,
} from './pcmpRequest.types';

// Requested Proposals (API LB-778): the approver's view of the open requests.

/** The tab keys with a fixed meaning; every other key is a category (`CODE` or `CODE:SOURCE`). */
export const PCMP_REQUESTED_ALL_TAB = 'ALL';
export const PCMP_REQUESTED_APPROVED_TAB = 'APPROVED';
export const PCMP_REQUESTED_PRE_BATCH_TAB = 'PRE_BATCH';
/** Not an API tab: the urgent batches have their own list. */
export const PCMP_REQUESTED_BATCHES_TAB = 'BATCHES';

export const pcmpRequestedTabSchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: z.enum(['ALL', 'APPROVED', 'PRE_BATCH', 'CATEGORY']),
  categoryCode: z.string().nullable(),
  source: z.enum(PCMP_CHANNELS).nullable(),
  count: z.number(),
  /** The proposed total of the whole tab, for "select all matching". */
  amount: z.number(),
});

export type PcmpRequestedTab = z.infer<typeof pcmpRequestedTabSchema>;

export const pcmpRequestedCountsSchema = z.object({
  /** The viewer is an approver (admin or MIS); everyone else reads only. */
  canAct: z.boolean(),
  tabs: z.array(pcmpRequestedTabSchema),
  batches: z.number(),
});

export type PcmpRequestedCounts = z.infer<typeof pcmpRequestedCountsSchema>;

export const pcmpRequestedItemSchema = pcmpRequestQueueItemSchema.extend({
  request: pcmpRequestSummarySchema,
  /** The MO's PCMP division; null = not mapped. */
  division: z.string().nullable(),
  region: z.string().nullable(),
  /** The viewer may decide this request (an approver, not yet decided). */
  canAct: z.boolean(),
  /** An approver may still mark it Processing. */
  canProcess: z.boolean(),
  /** Why approving it would be refused now; null when it can run or only records. */
  approveBlockedReason: z.string().nullable(),
  approveWarning: z.string().nullable(),
});

export type PcmpRequestedItem = z.infer<typeof pcmpRequestedItemSchema>;

export const pcmpDivisionTotalSchema = z.object({
  division: z.string(),
  count: z.number(),
  amount: z.number(),
});

export const pcmpRegionTotalSchema = pcmpDivisionTotalSchema.extend({ region: z.string() });

export const pcmpRequestedListSchema = z.object({
  tab: z.string(),
  canAct: z.boolean(),
  totalAmount: z.number(),
  /** Set on Pre-Batch and the split (Accounts / Marketing) category tabs. */
  divisionTotals: z.array(pcmpDivisionTotalSchema).nullable(),
  regionTotals: z.array(pcmpRegionTotalSchema).nullable(),
  requests: z.object({ items: z.array(pcmpRequestedItemSchema), meta: pcmpPageMetaSchema }),
});

export type PcmpRequestedList = z.infer<typeof pcmpRequestedListSchema>;

export interface PcmpRequestedParams extends PcmpQueueFilters {
  tab: string;
  page: number;
  limit: number;
}

export const pcmpRequestedSearchSchema = z.object({
  tab: z.string().max(60).optional().catch(undefined),
  ...pcmpQueueSearchShape,
});

export type PcmpRequestedSearch = z.infer<typeof pcmpRequestedSearchSchema>;

// Acting on requests.

export const PCMP_REQUEST_DECISIONS = ['PROCESS', 'APPROVE', 'REJECT'] as const;
export type PcmpRequestDecision = (typeof PCMP_REQUEST_DECISIONS)[number];

export type PcmpRequestActSelection =
  | { proposalIds: number[] }
  | {
      selectAllMatching: true;
      /** The tab the select-all covers: ALL or a category tab. */
      tab: string;
      filters: PcmpQueueFilters;
      excludedProposalIds: number[];
    };

export type PcmpRequestActPayload = {
  decision: PcmpRequestDecision;
  /** Optional, except to reject. */
  comment?: string | undefined;
} & PcmpRequestActSelection;

const actedSchema = z.object({
  proposalId: z.coerce.number(),
  requestId: z.coerce.number(),
  decision: z.enum(PCMP_REQUEST_DECISIONS),
  status: pcmpRequestStatusSchema,
  /** The category's action ran (false: only the decision was recorded). */
  executed: z.boolean(),
  eventId: z.coerce.number(),
});

export const pcmpRequestActResultSchema = z.object({
  decision: z.enum(PCMP_REQUEST_DECISIONS),
  requested: z.number(),
  done: z.array(actedSchema),
  skipped: z.array(pcmpSkippedProposalSchema),
  /** Marketing urgent batches the approvals opened. */
  urgentBatches: z.array(z.object({ batchNo: z.number(), channel: z.enum(PCMP_CHANNELS) })),
  /** Accounts proposals moved to Urgent Pre-Batch. */
  preBatched: z.number(),
});

export type PcmpRequestActResult = z.infer<typeof pcmpRequestActResultSchema>;

/** The single-proposal form (details page): one acted item. */
export const pcmpRequestActedSchema = actedSchema;
export type PcmpRequestActed = z.infer<typeof actedSchema>;
